import pino, { Level, LogEvent } from 'pino';
import * as Sentry from '@sentry/react';
import stringify from 'fast-safe-stringify';

/**
 * Initialize Sentry. Call once at app startup (e.g. in main.tsx).
 * The logger works without this — errors just go to console only.
 * Logs a warning and skips init if the DSN or environment is missing.
 */
export function initLogger(sentryDsn?: string, environment?: string) {
  if (!sentryDsn) {
    console.warn('[Sentry] DSN is not set; skipping initialization.');
    return;
  }
  if (!environment) {
    console.warn('[Sentry] environment is not set; skipping initialization.');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,
  });
}

/**
 * Identify (or clear) the current user for Sentry error attribution.
 * Pass null to clear the user — e.g. when they have limited tracking.
 */
export function setSentryUser(user: { id: string } | null) {
  if (!Sentry.isInitialized()) return;
  Sentry.setUser(user);
}

const toSentryLevel = (level: Level): Sentry.SeverityLevel => {
  switch (level) {
    case 'trace':
    case 'debug':
      return 'debug';

    case 'info':
      return 'info';

    case 'warn':
      return 'warning';

    case 'error':
      return 'error';

    case 'fatal':
      return 'fatal';

    default:
      console.warn(
        `Unable to convert level to Sentry level, defaulting to error. Unknown level: ${String(level)}`
      );
      return 'error';
  }
};

/**
 * Pino browser logger with Sentry transmit.
 *
 * Console output happens automatically via Pino's default browser behaviour.
 * When Sentry is initialised (via initLogger), warn+ logs are forwarded to Sentry.
 */
export const logger = pino({
  browser: {
    transmit: {
      level: (import.meta.env.VITE_SENTRY_LOG_LEVEL as Level | undefined) || 'warn',
      send(level: Level, logEvent: LogEvent) {
        if (!Sentry.isInitialized()) return;

        const firstMessage: unknown = logEvent.messages[0];
        const secondMessage: unknown = logEvent.messages[1];
        const optionalMessage = typeof secondMessage === 'string' ? secondMessage : undefined;

        if (firstMessage instanceof Error) {
          Sentry.captureException(firstMessage, {
            level: toSentryLevel(level),
            extra: {
              message: optionalMessage,
              ...(firstMessage.cause !== undefined && { cause: firstMessage.cause }),
            },
          });
        } else {
          const message = typeof firstMessage === 'string' ? firstMessage : stringify(firstMessage);

          Sentry.captureMessage(message, { level: toSentryLevel(level) });
        }
      },
    },
  },
});
